import dayjs from 'dayjs'
import { TFunction } from 'i18next'
import { DefaultTheme } from 'styled-components'
import { IBusinessOverviewRow } from '@query'
import { formatEurosWithOpts } from '@utils/formatCurrency'

type Urgency = 'high' | 'med' | null

export const deadlineDaysLeft = (row: IBusinessOverviewRow): number | null => {
  // next_deadline is null when the row's usership lacks the permission.
  if (row.next_deadline?.status !== 'upcoming' || !row.next_deadline.date) return null
  return dayjs(row.next_deadline.date).startOf('day').diff(dayjs().startOf('day'), 'day')
}

// Deadline-driven only: the tint marks something the user can act on
// (file the VAT/report), never overdue receivables they cannot influence.
// Graded by how close the due date is: red within two days, warning
// yellow within a week, nothing beyond that.
export const rowUrgency = (row: IBusinessOverviewRow): Urgency => {
  if (row.form === 'DEMO') return null
  const days = deadlineDaysLeft(row)
  if (days === null) return null
  if (days <= 2) return 'high'
  if (days <= 7) return 'med'
  return null
}

// Why the row is tinted, surfaced as a tooltip so nobody has to read the
// code to learn what the red accent means.
export const urgencyReason = (row: IBusinessOverviewRow, t: TFunction): string | null => {
  if (!rowUrgency(row)) return null
  const days = deadlineDaysLeft(row)
  const type = row.next_deadline?.type
  if (days === null || !type) return null
  const deadline = t(`businessesPage.table.deadline${type === 'fiscal' ? 'Fiscal' : 'Vat'}`)
  if (days < 0) return t('businessesPage.table.urgencyOverdue', { deadline, count: -days })
  if (days === 0) return t('businessesPage.table.urgencyToday', { deadline })
  return t('businessesPage.table.urgencyInDays', { deadline, count: days })
}

// Formatter construction is expensive; one instance per locale serves
// every deadline cell.
const deadlineFormatters: Record<string, Intl.DateTimeFormat> = {}

// Compact locale-aware day+month, e.g. "Aug 12" (en) / "12. elok." (fi):
// the fixed design pattern would force month-first ordering on fi/sv/de.
export const formatDeadlineDate = (date: string, locale: string): string => {
  deadlineFormatters[locale] ??= new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short'
  })
  return deadlineFormatters[locale].format(dayjs(date).toDate())
}

export const formatCents = (cents: number): string =>
  formatEurosWithOpts(cents, { avoidDecimals: true })

const hourFormatters: Record<string, Intl.NumberFormat> = {}

// Locale-aware decimal (e.g. "5,2 h" on fi/sv/de) to match the money cells.
export const formatHours = (hours: number, locale: string): string => {
  hourFormatters[locale] ??= new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  })
  return `${hourFormatters[locale].format(hours)} h`
}

const relativeFormatters: Record<string, Intl.RelativeTimeFormat> = {}

const TIME_AGO_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 86400],
  ['month', 30 * 86400],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60]
]

// Always "digit + unit" ("7 months ago" / "7 kuukautta sitten") in every
// locale — dayjs's fromNow drops the digit on singular units ("a month
// ago") and spells Finnish counts under ten out in words.
export const formatTimeAgo = (date: string, locale: string): string => {
  relativeFormatters[locale] ??= new Intl.RelativeTimeFormat(locale, { numeric: 'always' })
  const seconds = Math.max(1, dayjs().diff(dayjs(date), 'second'))
  for (const [unit, size] of TIME_AGO_UNITS) {
    if (seconds >= size) {
      return relativeFormatters[locale].format(-Math.floor(seconds / size), unit)
    }
  }
  return relativeFormatters[locale].format(-seconds, 'second')
}

export const initialsOf = (name?: string, fallback = ''): string =>
  (name || fallback)
    .replace(/\b(Oy|Oyj|Ky|Ay|ry|Tmi)\b/g, '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(word => word[0] || '')
    .join('')
    .toUpperCase()

export const automationColor = (pct: number | null, theme: DefaultTheme): string => {
  if (pct === null) return theme.colors.borderStrong
  // Grey until automation kicks in, then the same green as Time saved so
  // both automation metrics read alike.
  if (pct === 0) return theme.colors.iconGray
  return successDark(theme)
}

export const mix = (color: string, pct: number, base = 'white'): string =>
  `color-mix(in srgb, ${color} ${pct}%, ${base})`

export const successDark = (theme: DefaultTheme): string => mix(theme.colors.success, 62, 'black')

export const warningDark = (theme: DefaultTheme): string => mix(theme.colors.warning, 52, 'black')

export interface RowTint {
  accent: string
  background: string
  hoverBackground: string
}

// Urgency-driven row colors, shared by the table rows and the compact
// business-menu list.
export const rowTint = (row: IBusinessOverviewRow, theme: DefaultTheme): RowTint => {
  const urgency = rowUrgency(row)
  if (urgency === 'high') {
    return {
      accent: theme.colors.error,
      background: mix(theme.colors.error, 5),
      hoverBackground: mix(theme.colors.error, 9)
    }
  }
  if (urgency === 'med') {
    return {
      accent: theme.colors.warning,
      background: mix(theme.colors.warning, 9),
      hoverBackground: mix(theme.colors.warning, 15)
    }
  }
  return {
    accent: 'transparent',
    background: theme.colors.neutralWhite,
    hoverBackground: theme.colors.defaultHover
  }
}

// "1234567-8 · Limited company" subtitle under the business name; demo
// businesses show the practice-account label instead.
export const businessSubtitle = (row: IBusinessOverviewRow, t: TFunction): string =>
  row.form === 'DEMO'
    ? t('businessesPage.table.practiceAccount')
    : [
        row.business_id,
        row.form ? t(`constants.businessForm.${row.form}`, { defaultValue: '' }) : null
      ]
        .filter(Boolean)
        .join(' · ')
